import { useCallback, useMemo, useState } from 'react'
import { useTranslations } from '@npco/common-utils-translations'
import {
  COLOR,
  DecisionModal,
  DownloadIcon,
  Ellipsize,
  Lightbox,
  useIsMobileResolution,
} from '@npco/zeller-design-system'
import { useModalState } from 'design-system/Components/Modal'

import { ReactComponent as CloseIcon } from 'assets/svg/close-grey.svg'
import { ReactComponent as DeleteIcon } from 'assets/svg/trash.svg'
import { useWindowResolution } from 'hooks/useWindowResolution/useWindowResolution'
import { FileWithId } from 'components/File'
import { ImagePreview } from 'components/ImagePreview/ImagePreview'
import type { Image } from 'components/Images/Images.types'

import { useDeleteTransactionReceipt } from '../../../../../../../../features/EditDebitCardTransactionImages/edit-debit-card-transaction-images-modal/EditDebitCardTransactionImagesModal/UploadReceipts/UploadReceiptsModal/hooks/useDeleteTransactionReceipt'
import { imagePreviewModalTranslations } from './ImagePreviewModal.i18n'
import { displayDeleteImageErrorToast } from './ImagePreviewModal.utils'
import { LightboxSelector } from './LightboxSelector/LightboxSelector'

const BUTTON_HEADER_HEIGHT = 80
const TILE_SELECTOR_HEIGHT = 48
const MOBILE_IMAGE_CONTROLS_HEIGHT = 32

const HORIZONTAL_PADDING_DESKTOP_AND_TABLET = 80
const HORIZONTAL_PADDING_MOBILE = 32

const MARGIN_BETWEEN_TILE_SELECTOR_AND_IMAGE_CONTROL = 72
const MARGIN_BETWEEN_IMAAGE_CONTROL_AND_BOTTOM = 12

const IMAGE_CONTROL_VERTICAL_MARGIN =
  MARGIN_BETWEEN_TILE_SELECTOR_AND_IMAGE_CONTROL +
  MARGIN_BETWEEN_IMAAGE_CONTROL_AND_BOTTOM

interface Props {
  image: FileWithId
  transactionUuid: string
  isOpen: boolean
  onDelete: (fileWithId: FileWithId) => void
  onClose: () => void
  onNext: () => void
  onPrevious: () => void
  images: Image[]
  previewId: string | null
  setPreviewId: (id: string | null) => void
  onAdd: () => void
  areImageControlsEnabled?: boolean
  imageConfig: {
    currentIndex: number
    total: number
  }
}

export const ImagePreviewModal = ({
  image,
  transactionUuid,
  isOpen,
  onDelete,
  onClose,
  onNext,
  onPrevious,
  images,
  previewId,
  setPreviewId,
  onAdd,
  areImageControlsEnabled = false,
  imageConfig,
}: Props) => {
  const isMobile = useIsMobileResolution()

  const { width, height } = useWindowResolution()
  const [isDeletingTransactionImage, setIsDeletingTransactionImage] =
    useState(false)

  const previewHeight =
    height -
    BUTTON_HEADER_HEIGHT -
    TILE_SELECTOR_HEIGHT -
    (!isMobile ? 0 : MOBILE_IMAGE_CONTROLS_HEIGHT) -
    (!isMobile
      ? MARGIN_BETWEEN_TILE_SELECTOR_AND_IMAGE_CONTROL
      : IMAGE_CONTROL_VERTICAL_MARGIN)
  const previewWidth =
    width -
    (!isMobile
      ? HORIZONTAL_PADDING_DESKTOP_AND_TABLET
      : HORIZONTAL_PADDING_MOBILE)

  const { deleteTransactionReceipt } = useDeleteTransactionReceipt()

  const deleteFile = useCallback(() => {
    onDelete(image)
  }, [image, onDelete])

  const deleteImage = useCallback(async () => {
    try {
      setIsDeletingTransactionImage(true)
      await deleteTransactionReceipt(image.id, transactionUuid)
      deleteFile()
    } catch (error) {
      displayDeleteImageErrorToast()
    } finally {
      setIsDeletingTransactionImage(false)
    }
  }, [deleteTransactionReceipt, image.id, transactionUuid, deleteFile])

  // Use id instead of comparing entire file object
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fileUrl = useMemo(() => URL.createObjectURL(image.file), [image.id])

  const t = useTranslations(imagePreviewModalTranslations)

  const { currentIndex, total } = imageConfig
  const indexText = t('indexText', {
    currentIndex: currentIndex + 1,
    total,
  })

  const { isModalOpen, openModal, closeModal } = useModalState()

  return (
    <Lightbox isOpen={isOpen} ariaLabelledby="receipt-lightbox">
      <Lightbox.Header
        heading={image.file.name}
        index={indexText}
        headingId="receipt-lightbox"
      >
        <Lightbox.Actions>
          <Lightbox.Button
            onClick={openModal}
            icon={DeleteIcon}
            isLoading={isDeletingTransactionImage}
          >
            {!isMobile && t('delete')}
          </Lightbox.Button>
          <Lightbox.Download
            href={fileUrl}
            download={image.file.name}
            icon={
              <DownloadIcon
                color={COLOR.WHITE}
                hoverColor={COLOR.WHITE}
                height="16"
                width="16"
              />
            }
          >
            {!isMobile && t('download')}
          </Lightbox.Download>
          <Lightbox.Button onClick={onClose} icon={CloseIcon}>
            {!isMobile && t('close')}
          </Lightbox.Button>
        </Lightbox.Actions>
      </Lightbox.Header>
      <Lightbox.Content>
        <LightboxSelector
          areImageControlsEnabled={areImageControlsEnabled}
          onPrevious={onPrevious}
          onNext={onNext}
          previewId={previewId}
          setPreviewId={setPreviewId}
          onAdd={onAdd}
          images={images}
          indexText={indexText}
        >
          <ImagePreview
            image={image}
            width={previewWidth}
            height={previewHeight}
            objectFit="scale-down"
          />
          <DecisionModal
            isOpen={isModalOpen}
            onCancel={closeModal}
            title={t('title')}
            primaryButtonLabel={t('confirm')}
            secondaryButtonLabel={t('cancel')}
            onClickPrimary={deleteImage}
            onClickSecondary={closeModal}
            isLoadingPrimary={isDeletingTransactionImage}
          >
            <Ellipsize>
              {t('description', { fileName: image.file.name })}
            </Ellipsize>
          </DecisionModal>
        </LightboxSelector>
      </Lightbox.Content>
    </Lightbox>
  )
}
